import {
  Component,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
  OnDestroy,
  OnInit,
  viewChildren,
  ElementRef,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray, AbstractControl } from '@angular/forms';
import { Subject, timer } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

interface Recipient {
  name: string;
  upiId: string;
  initial: string;
}

interface TransferDetails {
  accountNumber: string;
  card: string;
  ifsc: string;
  fee: string;
  available: string;
}

interface SourceAccount {
  name: string;
  number: string;
  available: string;
  type: string;
}

@Component({
  selector: 'app-send-upi-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './send-upi-modal.html',
  styleUrl: './send-upi-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SendUpiModal implements OnInit, OnDestroy {
  close = output<void>();
  pinInputs = viewChildren<ElementRef<HTMLInputElement>>('pinInput');

  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();

  readonly step = signal<number>(1);
  readonly isProcessing = signal<boolean>(false);
  readonly showCancelConfirm = signal<boolean>(false);
  readonly isNoteInputVisible = signal<boolean>(false);
  readonly isAccountDropdownOpen = signal<boolean>(false);

  readonly upiIdSignal = signal<string>('');
  readonly finalAmount = signal<string>('0.00');
  readonly apiError = signal<string | null>(null);

  // Suggestions state
  readonly isSearchFocused = signal<boolean>(false);
  readonly recentContacts = signal<Recipient[]>([
    { name: 'Rahul Sharma', upiId: 'rahul@okaxis', initial: 'R' },
    { name: 'Priya Verma', upiId: 'priya@okhdfcbank', initial: 'P' },
    { name: 'Amit Kumar', upiId: 'amit.k@oksbi', initial: 'A' }
  ]);
  readonly filteredSuggestions = computed(() => {
    const term = this.upiIdSignal().toLowerCase();
    if (!term) return this.recentContacts();
    return this.recentContacts().filter(c =>
      c.name.toLowerCase().includes(term) ||
      c.upiId.toLowerCase().includes(term)
    );
  });

  // Validation signals for button states
  readonly canProceedStep1Signal = signal<boolean>(false);
  readonly canProceedStep2Signal = signal<boolean>(false);

  form!: FormGroup;

  readonly sourceAccounts: SourceAccount[] = [
    { name: 'Credit Card 1', number: '**** 1234', available: '₹6,280,050.75', type: 'Credit Card' },
    { name: 'HDFC Bank', number: '**** 5678', available: '₹1,45,000.00', type: 'Bank Account' },
    { name: 'SBI Bank', number: '**** 9012', available: '₹50,000.00', type: 'Bank Account' }
  ];

  readonly selectedAccount = signal<SourceAccount>(this.sourceAccounts[0]);

  // Static Data (Preserved from original logic, but made reactive)
  readonly recipient = signal<Recipient>({
    name: 'Mykola Ledenov',
    upiId: '', // Will be filled from form
    initial: 'M'
  });

  readonly transferDetails = signal<TransferDetails>({
    accountNumber: 'XXXX567891234',
    card: '**** 1234',
    ifsc: 'HDFC0001234',
    fee: '25.00',
    available: '₹6,280,050.75'
  });

  constructor() {
    this.initForm();
  }

  ngOnInit(): void {
    // Subscribe to form status changes
    this.form.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkStep1Validity();
      this.checkStep2Validity();
      // Clear API error if user changes PIN
      if (this.form.get('pin')?.dirty) {
        this.apiError.set(null);
      }
    });

    // UPI ID Value Changes
    this.form.get('upiId')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.upiIdSignal.set(val || '');
      // Don't show error immediately while typing
      if (this.form.get('upiId')?.invalid) {
        this.canProceedStep1Signal.set(false);
      } else {
        this.checkStep1Validity();
      }

      if (val && val.length > 3) {
        // If valid, update recipient preview
        if (this.form.get('upiId')?.valid) {
          // Mock name fetch or just use ID
          this.recipient.update(r => ({ ...r, upiId: val, name: 'New Recipient', initial: val[0].toUpperCase() }));
        }
      }
    });

    // Amount Value Changes
    this.form.get('amount')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.checkStep2Validity();

      if (val) {
        const numericVal = parseFloat(val);
        if (!isNaN(numericVal)) {
          const formatted = numericVal.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
          this.finalAmount.set(formatted);
        }
      } else {
        this.finalAmount.set('0.00');
      }
    });

    // Initial check
    this.checkStep1Validity();
    this.checkStep2Validity();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.form = this.fb.group({
      upiId: ['', [
        Validators.required,
        Validators.pattern(/^[a-zA-Z0-9.\-_]{2,}@[a-zA-Z]{2,}$/) // Basic UPI pattern
      ]],
      amount: ['', [
        Validators.required,
        Validators.pattern(/^\d+(\.\d{1,2})?$/),
        Validators.min(1)
      ]],
      // Step 4 PIN
      pin: this.fb.array(Array(6).fill('').map(() => this.fb.control('', [
        Validators.required,
        Validators.pattern(/^\d$/)
      ])))
    });
  }

  // --- Validation Helpers ---

  private checkStep1Validity(): void {
    if (!this.form) {
      this.canProceedStep1Signal.set(false);
      return;
    }
    const upiControl = this.form.get('upiId');
    // For Step 1, simple validity check
    this.canProceedStep1Signal.set(!!(upiControl?.valid && upiControl?.value));
  }

  private checkStep2Validity(): void {
    if (!this.form) {
      this.canProceedStep2Signal.set(false);
      return;
    }
    const amountControl = this.form.get('amount');
    if (!amountControl?.valid) {
      this.canProceedStep2Signal.set(false);
      return;
    }
    const amount = parseFloat(amountControl.value || '0');
    this.canProceedStep2Signal.set(amount > 0);
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  getFieldError(fieldName: string): string {
    const control = this.form.get(fieldName);
    if (control && control.invalid && (control.touched || control.dirty)) {
      if (control.errors?.['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (control.errors?.['pattern']) {
        if (fieldName === 'upiId') {
          return 'Invalid UPI ID format (e.g. name@bank)';
        }
        if (fieldName === 'amount') {
          return 'Enter a valid amount';
        }
        return 'Invalid format';
      }
      if (control.errors?.['min']) {
        return 'Amount must be at least ₹1';
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      'upiId': 'UPI ID',
      'amount': 'Amount'
    };
    return labels[fieldName] || fieldName;
  }

  get pinControls(): AbstractControl[] {
    return (this.form.get('pin') as FormArray).controls;
  }

  // --- UI Actions ---

  toggleNoteInput(): void {
    this.isNoteInputVisible.update(v => !v);
  }

  toggleAccountDropdown(): void {
    this.isAccountDropdownOpen.update(v => !v);
  }

  selectAccount(account: SourceAccount): void {
    this.selectedAccount.set(account);
    this.isAccountDropdownOpen.set(false);
    this.transferDetails.update(d => ({
      ...d,
      card: account.number,
      available: account.available
    }));
  }

  // --- Step Navigation ---

  nextStep(): void {
    const currentStep = this.step();

    if (currentStep === 1) {
      const vidId = this.form.get('upiId');
      vidId?.markAsTouched();
      vidId?.updateValueAndValidity();

      this.checkStep1Validity();
      if (!this.canProceedStep1Signal()) return;

      // If proceeding, ensure we set recipient details if chosen from suggest or typed
      if (this.recipient().upiId !== vidId?.value) {
        this.recipient.update(r => ({ ...r, upiId: vidId?.value, name: 'Unknown User', initial: vidId?.value?.[0]?.toUpperCase() }));
      }
    }

    if (currentStep === 2) {
      const amount = this.form.get('amount');
      amount?.markAsTouched();
      amount?.updateValueAndValidity();

      this.checkStep2Validity();
      if (!this.canProceedStep2Signal()) return;
    }

    if (currentStep === 4) {
      const pinArray = this.form.get('pin') as FormArray;
      pinArray.markAsTouched(); // Mark the array itself as touched for isFieldInvalid('pin')
      pinArray.controls.forEach(c => c.markAsTouched());

      if (pinArray.invalid) return;

      // Simulate a small delay for "verifying" to feel more professional?
      // For now proceed to processing
      this.processTransaction();
      return;
    }

    if (currentStep < 5) {
      this.step.update(s => s + 1);
    } else {
      this.confirmCancel();
    }
  }

  prevStep(): void {
    if (this.step() > 1) {
      this.step.update(s => s - 1);
    }
  }

  processTransaction(): void {
    // Simulate PIN verification
    const pinValue = (this.form.get('pin') as FormArray).value.join('');

    this.step.set(5);
    this.isProcessing.set(true);

    timer(1500)
      .pipe(
        finalize(() => {
          this.isProcessing.set(false);
        })
      )
      .subscribe(() => {
        // Mock validation: fail if PIN is '000000'
        if (pinValue === '000000') {
          this.step.set(4);
          this.apiError.set('Incorrect PIN. Please try again.');
          // Clear PIN inputs for UX?
          (this.form.get('pin') as FormArray).reset();
          setTimeout(() => this.pinInputs()[0]?.nativeElement.focus());
        }
      });
  }

  onClose(): void {
    const currentStep = this.step();
    if (currentStep === 1 || currentStep === 5) {
      this.confirmCancel();
    } else {
      this.showCancelConfirm.set(true);
    }
  }

  confirmCancel(): void {
    this.close.emit();
  }

  closeCancelConfirm(): void {
    this.showCancelConfirm.set(false);
  }

  // --- Visual Formatting ---

  formatAmount(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^\d.]/g, '');

    // Prevent multiple decimal points
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit decimal places to 2
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }

    const control = this.form.get('amount');
    if (control && value !== control.value) {
      control.setValue(value, { emitEvent: true });
    }
  }

  // --- PIN Handling ---

  onPinInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value && index < 5) {
      const inputs = this.pinInputs();
      if (inputs[index + 1]) {
        inputs[index + 1].nativeElement.focus();
      }
    }
  }

  onPinBackspace(index: number, event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    const input = keyboardEvent.target as HTMLInputElement;
    const value = input.value;
    if (!value && index > 0) {
      const inputs = this.pinInputs();
      if (inputs[index - 1]) {
        inputs[index - 1].nativeElement.focus();
      }
    }
  }

  // --- Search helpers ---
  onSearchFocus() {
    this.isSearchFocused.set(true);
  }

  onSearchBlur() {
    // Delay to allow click on suggestion
    setTimeout(() => this.isSearchFocused.set(false), 200);
  }

  clearSearch() {
    this.form.get('upiId')?.setValue('');
    this.isSearchFocused.set(false);
  }

  selectSuggestion(contact: Recipient) {
    this.form.get('upiId')?.setValue(contact.upiId);
    this.recipient.set(contact);
    this.nextStep();
  }

  trackByFn(index: number): number {
    return index;
  }
}